import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, message } from 'antd';
import { 
  MobileOutlined, 
  AppstoreOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined 
} from '@ant-design/icons';
import api from '../utils/api';

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    totalAPKs: 0,
    pendingCommands: 0
  });
  const [recentDevices, setRecentDevices] = useState([]);
  const [recentCommands, setRecentCommands] = useState([]);

  const isDeviceOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const lastSeenTime = new Date(lastSeen).getTime();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    return (now - lastSeenTime) < oneHour;
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [devicesRes, apksRes, commandsRes] = await Promise.all([
        api.get('/devices'),
        api.get('/apks'),
        api.get('/commands/history')
      ]);

      const devices = devicesRes.data.data || [];
      const apks = apksRes.data.data || [];
      const commands = commandsRes.data.data || [];

      const onlineCount = devices.filter(d => isDeviceOnline(d.lastSeen)).length;
      const pendingCount = commands.filter(c => c.status === 'pending').length;

      setStats({
        totalDevices: devices.length,
        onlineDevices: onlineCount,
        totalAPKs: apks.length,
        pendingCommands: pendingCount
      });

      setRecentDevices(devices.slice(0, 5));
      setRecentCommands(commands.slice(0, 5));
    } catch (error) {
      message.error('获取数据失败');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const deviceColumns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      key: 'deviceName'
    },
    {
      title: '设备型号',
      dataIndex: 'deviceModel',
      key: 'deviceModel'
    },
    {
      title: 'Android版本',
      dataIndex: 'androidVersion',
      key: 'androidVersion'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const actualStatus = isDeviceOnline(record.lastSeen) ? 'online' : 'offline';
        return (
          <Tag color={actualStatus === 'online' ? 'green' : 'red'}>
            {actualStatus === 'online' ? '在线' : '离线'}
          </Tag>
        );
      }
    },
    {
      title: '最后在线',
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      render: (date) => new Date(date).toLocaleString('zh-CN')
    }
  ];

  const commandColumns = [
    {
      title: '指令ID',
      dataIndex: 'commandId',
      key: 'commandId',
      ellipsis: true
    },
    {
      title: '设备ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
      ellipsis: true
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeMap = {
          install: '安装',
          uninstall: '卸载',
          update: '更新',
          wipe: '擦除',
          lock: '锁定'
        };
        return typeMap[type] || type;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          pending: 'orange',
          in_progress: 'blue',
          success: 'green',
          failed: 'red',
          cancelled: 'gray'
        };
        const labelMap = {
          pending: '待执行',
          in_progress: '执行中',
          success: '成功',
          failed: '失败',
          cancelled: '已取消'
        };
        return <Tag color={colorMap[status]}>{labelMap[status] || status}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString('zh-CN')
    }
  ];

  return (
    <div>
      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic
              title="设备总数"
              value={stats.totalDevices}
              prefix={<MobileOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="在线设备"
              value={stats.onlineDevices}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="APK总数"
              value={stats.totalAPKs}
              prefix={<AppstoreOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待执行指令"
              value={stats.pendingCommands}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="最近设备" bordered={false}>
            <Table
              dataSource={recentDevices}
              columns={deviceColumns}
              rowKey="deviceId"
              loading={loading}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="最近指令" bordered={false}>
            <Table
              dataSource={recentCommands}
              columns={commandColumns}
              rowKey="commandId"
              loading={loading}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
